/**
 * Native Android notifications helper.
 *
 * The Android shell (WebAppActivity) injects `window.AndroidNotifier` — a
 * JavascriptInterface backed by NotificationBridge.java. On the plain web /
 * PWA where that object is absent, all helpers become no-ops so calling code
 * doesn't need to branch.
 *
 * We deliberately keep the surface tiny (available / notify / cancel) — the
 * heavy lifting (channel setup, PendingIntent, permission prompts) is handled
 * natively in Java.
 */

const _bridge = () => (typeof window !== 'undefined' ? window.AndroidNotifier : null);

const _parse = (raw) => {
  try { return JSON.parse(raw || '{}'); } catch { return {}; }
};

/** True when running inside the RecargameYa Android shell. */
export const isNativeNotifierAvailable = () => {
  const b = _bridge();
  if (!b) return false;
  try { return String(b.isAvailable()) === 'true'; } catch { return false; }
};

/** True if POST_NOTIFICATIONS is granted (always true pre-Android 13). */
export const hasNativeNotificationPermission = () => {
  const b = _bridge();
  if (!b) return false;
  try { return String(b.hasPermission()) === 'true'; } catch { return false; }
};

/** Prompt the user for POST_NOTIFICATIONS (Android 13+). No-op if pre-13. */
export const requestNativeNotificationPermission = () => {
  const b = _bridge();
  if (!b) return;
  try { b.requestPermission(); } catch { /* ignore */ }
};

/**
 * Post (or replace) a native system notification. Using the same tag+id
 * updates the same visible notification instead of stacking a new one.
 *
 * @returns {{ok: boolean, error?: string}}
 */
export const nativeNotify = ({ title, body, tag = 'default', id = 1 }) => {
  const b = _bridge();
  if (!b) return { ok: false, error: 'bridge_missing' };
  try {
    return _parse(b.notify(title || 'RecargameYa', body || '', tag, id));
  } catch (e) {
    return { ok: false, error: (e && e.message) || 'unknown' };
  }
};

/** Dismiss the notification identified by tag+id. */
export const nativeCancel = (tag = 'default', id = 1) => {
  const b = _bridge();
  if (!b) return;
  try { b.cancel(tag, id); } catch { /* ignore */ }
};

/** Dismiss ALL notifications posted by the app. */
export const nativeCancelAll = () => {
  const b = _bridge();
  if (!b) return;
  try { b.cancelAll(); } catch { /* ignore */ }
};

/** Returns the cached FCM device token from the native shell, or "" if not
 *  yet fetched. Callers should poll (with a small delay) after first launch
 *  because Firebase resolves the token asynchronously. */
export const getNativeFcmToken = () => {
  const b = _bridge();
  if (!b || typeof b.getFcmToken !== 'function') return '';
  try { return String(b.getFcmToken() || ''); } catch { return ''; }
};

/** Kick off a Firebase token fetch; result becomes readable via getNativeFcmToken. */
export const refreshNativeFcmToken = () => {
  const b = _bridge();
  if (!b || typeof b.refreshFcmToken !== 'function') return;
  try { b.refreshFcmToken(); } catch { /* ignore */ }
};
